import React, { useState } from 'react';
import {
  Copy, QrCode, X, Download, Linkedin, Twitter, Mail,
  BadgeCheck, User, Zap
} from 'lucide-react';

const ShareModal = ({ isOpen, onClose, profile }) => {
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const profileUrl = window.location.href;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    {
      name: "LinkedIn",
      icon: Linkedin,
      color: "bg-[#0A66C2]",
      hoverColor: "hover:bg-[#004182]",
      link: `https://www.linkedin.com/sharing/share-offsite/?url=${profileUrl}`,
    },
    {
      name: "Twitter",
      icon: Twitter,
      color: "bg-[#1DA1F2]",
      hoverColor: "hover:bg-[#0c85d0]",
      link: `https://twitter.com/intent/tweet?url=${profileUrl}&text=Check out ${profile.firstName}'s profile`,
    },
    {
      name: "WhatsApp",
      icon: Zap,
      color: "bg-[#25D366]",
      hoverColor: "hover:bg-[#128C7E]",
      link: `https://wa.me/?text=Check out ${profile.firstName}'s profile: ${profileUrl}`,
    },
    {
      name: "Email",
      icon: Mail,
      color: "bg-[#EA4335]",
      hoverColor: "hover:bg-[#d33426]",
      link: `mailto:?subject=Check out ${profile.firstName}'s profile&body=Here's the link: ${profileUrl}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Share Profile
                  </h3>
                  <button
                    onClick={onClose}
                    className="rounded-full p-1 hover:bg-gray-100"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Profile Preview */}
                <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    {profile.personalInfo?.avatar ? (
                      <img
                        src={profile.personalInfo.avatar}
                        alt={profile.firstName}
                        className="h-12 w-12 rounded-full"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="h-6 w-6 text-blue-500" />
                      </div>
                    )}
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {profile.firstName} {profile.lastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {profile.department} • {profile.college.name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Share Options */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {shareOptions.map((option) => (
                    <a
                      key={option.name}
                      href={option.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center justify-center px-4 py-2 rounded-lg text-white ${option.color} ${option.hoverColor} transition-colors`}
                    >
                      <option.icon className="h-5 w-5 mr-2" />
                      {option.name}
                    </a>
                  ))}
                </div>

                {/* Copy Link */}
                <div className="relative mb-6">
                  <div className="flex">
                    <input
                      type="text"
                      readOnly
                      value={profileUrl}
                      className="flex-1 py-2 px-3 rounded-l-lg border border-gray-300 bg-gray-50 text-sm"
                    />
                    <button
                      onClick={handleCopy}
                      className={`px-4 py-2 rounded-r-lg border border-l-0 border-gray-300 ${
                        copied
                          ? "bg-green-50 text-green-600"
                          : "bg-gray-50 text-gray-600"
                      } hover:bg-gray-100 transition-colors`}
                    >
                      {copied ? (
                        <span className="flex items-center">
                          <BadgeCheck className="h-5 w-5 mr-1" />
                          Copied!
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Copy className="h-5 w-5 mr-1" />
                          Copy
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Additional Options */}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setShowQR(!showQR)}
                    className="flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    <QrCode className="h-5 w-5 mr-2 text-gray-500" />
                    Show QR Code
                  </button>
                  <button
                    onClick={() => {
                      // Handle PDF download
                    }}
                    className="flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    <Download className="h-5 w-5 mr-2 text-gray-500" />
                    Download PDF
                  </button>
                </div>

                {/* QR Code Section */}
                {showQR && (
                  <div className="mt-6 flex justify-center">
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <QrCode className="h-24 w-24 text-gray-400" />
                      </div>
                      <p className="mt-2 text-sm text-gray-500 text-center">
                        Scan to view profile
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;